// React hook for fetching the current user's organisation membership
// and populating the Zustand store with organisation data.
// Should be mounted once near the app root (e.g., in ProtectedLayout).

import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../contexts/AuthContext";
import { useAppStore } from "../stores/appStore";
import { getUserOrganisationMembership } from "../services/organisationApi";
import { logger } from "../services/logger";
import { generateUUID } from "../utils/uuid";

export function useUserOrganisation() {
  const { user } = useAuth();
  const { setOrganisation } = useAppStore();

  const query = useQuery({
    queryKey: ["user-organisation-membership"],
    queryFn: async () => {
      const correlationId = generateUUID();

      logger.info("Fetching user organisation membership", {
        userId: user?.id,
        correlationId,
      });

      try {
        const data = await getUserOrganisationMembership();

        logger.info("User organisation membership fetched successfully", {
          userId: user?.id,
          organisationId: data?.organisationId ?? null,
          correlationId,
        });

        return data;
      } catch (error) {
        logger.error(
          "Failed to fetch user organisation membership",
          error as Error,
          {
            userId: user?.id,
            correlationId,
          },
        );

        throw error;
      }
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error: unknown) => {
      const status = (error as { response?: { status?: number } })?.response
        ?.status;
      if (status === 401 || status === 403) {
        return false;
      }
      return failureCount < 2;
    },
  });

  useEffect(() => {
    if (query.data) {
      setOrganisation({
        id: query.data.organisationId,
        name: query.data.organisationName,
        role: {
          id: query.data.roleId,
          slug: query.data.roleSlug,
        },
        auth: query.data.auth,
      });
    } else if (query.data === null && !query.isLoading) {
      setOrganisation(null);
    }
  }, [query.data, query.isLoading, setOrganisation]);

  return {
    isLoading: query.isLoading,
    refetch: query.refetch,
  };
}
